/**
 * is-payload.ts — normaliza el payload crudo de un informe IS (Informe de
 * Servicio) a la forma tipada IsPayload, rellenando con valores vacíos las
 * secciones ausentes. Espeja a ie-payload.ts.
 *
 * El editor mantiene SIEMPRE el payload completo en estado; al guardar se envía
 * entero vía updateInformePayload (no dependemos de merge en el backend).
 */
import type {
  IeDatosGenerales,
  IeEquipo,
  IeFoto,
  IsActividad,
  IsEngrase,
  IsPayload, // REQ-8a: antecedentes removed from IsPayload
  TablaColumna,
  TablaGenerica,
  ZonaEngrase,
} from "../../types.js";
import { PANEL_DEFAULT_TITULOS } from "../ie/ie-payload.js";
import { DEFAULT_CARGO } from "../paso-datos-generales-helpers.js";

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function num(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function datosGenerales(raw: unknown, clienteFallback: string): IeDatosGenerales {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const cliente = str(o["cliente"]) || clienteFallback;
  return {
    cliente,
    atencion: str(o["atencion"]),
    supervisor: str(o["supervisor"]),
    cargo: str(o["cargo"]) || DEFAULT_CARGO,
    fechaInicio: str(o["fechaInicio"]),
    fechaCierre: str(o["fechaCierre"]),
    referencia: str(o["referencia"]),
    lugar: str(o["lugar"]),
    ocCliente: str(o["ocCliente"]),
    otMpsa: str(o["otMpsa"]),
    emision: str(o["emision"]),
    anexo: str(o["anexo"]),
    revision: num(o["revision"]),
  };
}

function equipos(raw: unknown): IeEquipo[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((e, i): IeEquipo => {
    const o = (e && typeof e === "object" ? e : {}) as Record<string, unknown>;
    const item = o["item"];
    return {
      item: typeof item === "number" && Number.isFinite(item) ? item : i + 1,
      equipo: str(o["equipo"]),
      marca: str(o["marca"]),
      modelo: str(o["modelo"]),
      serie: str(o["serie"]),
      designacion: str(o["designacion"]),
    };
  });
}

/** Panel fotográfico: 7 slots por defecto (igual que IE) en un IS nuevo. */
function panelFotografico(raw: unknown): IeFoto[] {
  if (raw === undefined) {
    return PANEL_DEFAULT_TITULOS.map((titulo): IeFoto => ({ titulo, descripcion: "", url: "" }));
  }
  if (!Array.isArray(raw)) return [];
  return raw.map((f): IeFoto => {
    const o = (f && typeof f === "object" ? f : {}) as Record<string, unknown>;
    return { url: str(o["url"]), titulo: str(o["titulo"]), descripcion: str(o["descripcion"]) };
  });
}

function actividades(raw: unknown): IsActividad[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((a): IsActividad => {
    const o = (a && typeof a === "object" ? a : {}) as Record<string, unknown>;
    const fotoUrl = str(o["fotoUrl"]);
    // fotoUrl es opcional: solo se incluye si hay valor.
    return fotoUrl ? { descripcion: str(o["descripcion"]), fotoUrl } : { descripcion: str(o["descripcion"]) };
  });
}

/** Conclusiones/recomendaciones fijas del IS oficial (template MARCO). Se
 *  prellenan en todo IS (nuevo o continuación IE→IS); el técnico puede
 *  editarlas, borrarlas o agregar propias. */
export const CONCLUSIONES_IS_DEFAULT: readonly string[] = [
  "La información completa de seguridad general y específica se encuentra en el manual de usuario, complementa la información del presente documento.",
  "No altere ni modifique ninguna pieza de este sistema a menos que sea aprobado por fábrica, consultar el certificado de garantía entregado por MARCO PERUANA.",
  "Utilizar los repuestos señalados por MARCO PERUANA en la lista de componentes del manual de usuario; el uso de componentes ajenos puede comprometer el normal funcionamiento del sistema.",
  "Se recomienda consultar el plan de mantenimiento general ubicado en el manual de usuario y solicitar el repuesto a MARCO PERUANA.",
];

function conclusiones(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((c) => str(c));
}

/**
 * Normalizes a generic table from raw data.
 * Seeds empty table if raw is missing or invalid — never crashes.
 */
function tablaGenerica(raw: unknown): TablaGenerica {
  const EMPTY: TablaGenerica = { columnas: [], filas: [] };
  if (!raw || typeof raw !== "object") return EMPTY;
  const o = raw as Record<string, unknown>;

  const columnas: TablaColumna[] = Array.isArray(o["columnas"])
    ? o["columnas"].map((c): TablaColumna => {
        const col = (c && typeof c === "object" ? c : {}) as Record<string, unknown>;
        return { id: str(col["id"]), titulo: str(col["titulo"]) };
      })
    : [];

  const filas: Record<string, string>[] = Array.isArray(o["filas"])
    ? o["filas"].map((f): Record<string, string> => {
        if (!f || typeof f !== "object") return {};
        return Object.fromEntries(
          Object.entries(f as Record<string, unknown>).map(([k, v]) => [k, str(v)]),
        );
      })
    : [];

  return { columnas, filas };
}

/**
 * Normalizes a zonas array from raw payload data.
 * Each zone must have titulo, descripcion, and tabla.
 * Non-array input or zones that don't conform to the new shape → seed empty.
 *
 * Old-data policy: legacy zona/inyector objects (nombre + inyectores) are NOT
 * ZonaEngrase-shaped, so they normalize to an empty array — no crash.
 */
function zonas(raw: unknown): ZonaEngrase[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((z): ZonaEngrase[] => {
    if (!z || typeof z !== "object") return [];
    const o = z as Record<string, unknown>;
    // Only accept zones that have at least a titulo string (new shape)
    if (typeof o["titulo"] !== "string") return [];
    return [
      {
        titulo: str(o["titulo"]),
        descripcion: str(o["descripcion"]),
        tabla: tablaGenerica(o["tabla"]),
      },
    ];
  });
}

/**
 * Normalizes an engrase object from raw payload data.
 *
 * PR-D (ADR-D1): old-data tolerance —
 *   - frecuenciaLubricacion (legacy) → mapped to tiempoReposo
 *   - legacy flat tabla → abandoned; zonas seeds empty
 *   - missing tiempoReposo/tiempoLubricacion → defaults to empty string
 *   - missing zonas → seeds empty array
 *
 * ponytail: old engrase flat tabla / legacy zona/inyector shapes are abandoned on
 * normalize; ceiling = if a historical engrase IS must reprint exactly, add a
 * one-time flattener before calling toIsPayload.
 */
function engrase(raw: unknown): IsEngrase {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const tipoPresion =
    o["tipoPresion"] === "hidraulico"
      ? "hidraulico"
      : o["tipoPresion"] === "electrico"
        ? "electrico"
        : "neumatico";

  // tiempoReposo: prefer new field; fall back to legacy frecuenciaLubricacion
  const tiempoReposo = str(o["tiempoReposo"]) || str(o["frecuenciaLubricacion"]);

  return {
    tiempoReposo,
    tiempoLubricacion: str(o["tiempoLubricacion"]),
    tipoPresion,
    presionTrabajo: str(o["presionTrabajo"]),
    presionLubricacion: str(o["presionLubricacion"]),
    // zonas: normalize from payload or seed empty (flat tabla / old shapes are abandoned)
    zonas: zonas(o["zonas"]),
  };
}

/** Construye un IsPayload completo a partir del payload crudo del informe. */
export function toIsPayload(raw: Record<string, unknown>, clienteFallback: string): IsPayload {
  const tipoServicio = raw["tipoServicio"] === "engrase" ? "engrase" : "otro";
  const base: IsPayload = {
    datosGenerales: datosGenerales(raw["datosGenerales"], clienteFallback),
    equipos: equipos(raw["equipos"]),
    // IS nuevo (clave ausente) → 7 slots por defecto; si ya existe (aunque sea []), respeta.
    panelFotografico: panelFotografico(raw["panelFotografico"]),
    // REQ-8a: antecedentes removed. Legacy payloads with antecedentes are simply ignored.
    // IS nuevo → vacío; el useEffect en InformeEditor sincroniza desde panelFotografico.descripcion.
    actividades: actividades(raw["actividades"] ?? []),
    // IS nuevo (clave ausente) → prellena las 4 conclusiones fijas; si ya existe
    // (aunque sea []), respeta lo guardado (no re-siembra).
    conclusionesRecomendaciones:
      raw["conclusionesRecomendaciones"] === undefined
        ? [...CONCLUSIONES_IS_DEFAULT]
        : conclusiones(raw["conclusionesRecomendaciones"]),
    tipoServicio,
  };
  // El bloque engrase solo existe cuando el servicio es de tipo "engrase".
  return tipoServicio === "engrase" ? { ...base, engrase: engrase(raw["engrase"]) } : base;
}
